import { Button, Flex } from 'antd'
import { TableColumnsType } from 'antd/lib'
import { useTheme } from 'antd-style'
import { Dispatch, SetStateAction, useMemo } from 'react'

import {
  Permissions,
  PersonTransferRequestInstitutionListItemDto,
  PersonTransferRequestPagedListItem,
  PersonTransferRequestStatus
} from 'src/api/requests'
import { Text } from 'src/components'
import {
  transferStatusColorMap,
  TransferStatusTag
} from 'src/features/transfers'
import { useUser } from 'src/hooks'
import { formatDate } from 'src/libs'
import { useLocales } from 'src/locales'
import { formatIdnp } from 'src/utils'

type Props = {
  setModalState: Dispatch<
    SetStateAction<{
      type: 'approve' | 'reject' | null
      id?: number
    }>
  >
}

const acceptedStatuses = [
  PersonTransferRequestStatus?.ACCEPTED,
  PersonTransferRequestStatus.REJECTED,
  PersonTransferRequestStatus.TRANSFERRED
]

export const useTransferColumns = ({ setModalState }: Props) => {
  const { t } = useLocales()

  const { userData, hasPermission } = useUser()

  const palette = useTheme()
  const isAdmin = hasPermission(
    Permissions.PERSONS_TRANSFER_REQUEST_MANAGE_INSTITUTION
  )

  const columns = useMemo(
    (): TableColumnsType<PersonTransferRequestPagedListItem> => [
      {
        key: 'simeCode',
        title: t('common.sime_code'),
        dataIndex: ['person', 'simeCode']
      },
      {
        key: 'tarvCode',
        title: t('common.tarv_code'),
        dataIndex: ['person', 'tarvCode']
      },
      {
        key: 'idnp',
        title: t('form.idnp'),
        render: ({ person }) => (person?.idnp ? formatIdnp(person?.idnp) : '-')
      },
      {
        key: 'lastName',
        title: t('form.last_name'),
        dataIndex: ['person', 'lastName']
      },
      {
        key: 'firstName',
        title: t('form.first_name'),
        dataIndex: ['person', 'firstName']
      },
      {
        key: 'gender',
        title: t('form.gender'),
        dataIndex: ['person', 'gender']
      },
      {
        key: 'birth',
        title: t('form.birth_date'),
        render: ({ person }) =>
          person?.birthDate ? formatDate(person?.birthDate) : '-'
      },
      {
        key: 'reason',
        title: t('transfer.transfer_patient_reason'),
        render: ({ notes }) => notes ?? '-'
      },
      {
        key: 'transfer',
        title: t('transfer.transfer'),
        render: ({ destinationInstitution, sourceInstitution, status }) => {
          return (
            <Flex vertical>
              <Text color={palette.colorTextTertiary}>
                {sourceInstitution ? sourceInstitution?.name : '-'}
              </Text>
              <Text
                color={
                  transferStatusColorMap[status as PersonTransferRequestStatus]
                }
              >
                {destinationInstitution ? destinationInstitution?.name : '-'}
              </Text>
            </Flex>
          )
        }
      },
      {
        key: 'status',
        title: t('form.status'),
        render: ({ status }) => <TransferStatusTag status={status} />
      },
      ...(isAdmin
        ? [
            {
              key: 'actions',
              title: t('common.action'),
              render: ({
                id,
                status,
                destinationInstitution,
                sourceInstitution
              }: {
                id: number
                status: PersonTransferRequestStatus
                destinationInstitution: PersonTransferRequestInstitutionListItemDto
                sourceInstitution: PersonTransferRequestInstitutionListItemDto
              }) => {
                const isProcessed = acceptedStatuses?.includes(status)

                const isCurrentUserRelated =
                  userData?.currentLegalEntityId ===
                    destinationInstitution?.id ||
                  userData?.currentLegalEntityId === sourceInstitution?.id

                const isUserApproved =
                  (userData?.currentLegalEntityId ===
                    destinationInstitution?.id &&
                    destinationInstitution?.isApproved) ||
                  (userData?.currentLegalEntityId === sourceInstitution?.id &&
                    sourceInstitution?.isApproved)

                return !isProcessed && isCurrentUserRelated ? (
                  isUserApproved ? (
                    <Text>{t('transfer.transfer_approved')}</Text>
                  ) : (
                    <Flex gap={8}>
                      <Button
                        type="primary"
                        size="small"
                        danger
                        onClick={() =>
                          setModalState({
                            type: 'reject',
                            id
                          })
                        }
                      >
                        {t('transfer.reject')}
                      </Button>

                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          setModalState({
                            type: 'approve',
                            id
                          })
                        }
                      >
                        {t('common.confirm')}
                      </Button>
                    </Flex>
                  )
                ) : (
                  ''
                )
              }
            }
          ]
        : [])
    ],
    [t, isAdmin]
  )

  return { columns }
}
